import { useMemo, useState } from "react";
import {
  FileText,
  Target,
  Calendar,
  Pill,
  ClipboardList,
  Trash2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { TreatmentPlanModal } from "../../../TreatmentPlan/TreatmentPlanModal";
import {
  deleteTreatmentPlanAction,
  getPatientActiveTreatmentPlan,
} from "@/dispatchers/treatmentPlanDispatchers";
import {
  getPatientConsultationByConsultationId,
  removePatientAIChatMessageByPlanId,
} from "@/dispatchers/patientDispatchers";
import { getTargetDirectionSymbol } from "@/lib/treatmentPlan/treatmentPlanData";
interface TreatmentPlanGoal {
  id?: string;
  description: string;
  targetDate?: string;
  status?: string;
  targetValue?: string;
  notes?: string;
  targetDirection?: string;
}

interface TreatmentPlanPrescription {
  medication?: string;
  dosage?: string;
  quantity?: string;
  additionalCount?: number;
}

interface TreatmentPlanAppointment {
  date?: string;
  time?: string;
  type?: string;
}

interface TreatmentPlanOverview {
  name: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  totalActions?: number;
  goalsCount?: number;
  appointmentsCount?: number;
  prescriptionsCount?: number;
  savedAt?: string;
}

interface TreatmentPlanItem {
  details?: { payload?: string; [key: string]: any } | null;
  displayTimestamp?: string;
  recordedAt?: string;
  isDraft?: boolean;
  [key: string]: any;
}

interface TreatmentPlanActionItemsProps {
  items: TreatmentPlanItem[];
  isDarkMode: boolean;
  onPlanClick?: (details: any) => void;
  /** Separate delete handler — does NOT use the generic onDeleteAction */
  onDeletePlan?: () => void;
  consultationId?: string;
  consultationData?: any;
}

function parsePlanPayload(item: TreatmentPlanItem): any {
  try {
    const raw = item?.details?.payload;
    if (!raw) return item?.details || {};
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return parsed?.treatmentPlan ?? parsed;
  } catch {
    return item?.details || {};
  }
}

const statusIntToString: Record<number, string> = {
  1: "active",
  2: "completed",
  3: "paused",
  4: "cancelled",
};

function buildOverview(plan: any): TreatmentPlanOverview {
  const rawStatus = plan?.status;
  const status =
    typeof rawStatus === "number"
      ? (statusIntToString[rawStatus] ?? "active")
      : rawStatus || "active";

  return {
    name: plan?.name || "Unnamed Plan",
    description: plan?.description || "",
    startDate: plan?.startDate || "",
    endDate: plan?.endDate || "",
    status,
    // ...rest unchanged
    totalActions:
      (plan?.goals?.length || 0) +
      (plan?.followUpAppointments?.length || 0) +
      (plan?.linkedPrescriptions?.length || 0),
    goalsCount: plan?.goals?.length || 0,
    appointmentsCount: plan?.followUpAppointments?.length || 0,
    prescriptionsCount:
      plan?._enrichedPrescriptions?.length ||
      plan?.linkedPrescriptions?.length ||
      0,
    savedAt: plan?.savedAt || plan?.updatedAt || "",
  };
}

const goalStatusIntToString: Record<number, string> = {
  1: "pending",
  2: "in-progress",
  3: "achieved",
  4: "abandoned",
};

function buildGoals(plan: any): TreatmentPlanGoal[] {
  return (plan?.goals || []).map((g: any) => {
    const rawStatus = g.status;
    const status =
      typeof rawStatus === "number"
        ? (goalStatusIntToString[rawStatus] ?? "pending")
        : rawStatus || "pending";

    return {
      id: g.id,
      description: g.description || "—",
      targetDate: g.targetDate
        ? new Date(g.targetDate).toLocaleDateString()
        : "—",
      status,
      targetValue: g.targetValue || "",
      // ← ADD
      targetDirection:
        typeof g.targetDirection === "number"
          ? (({ 1: "AtOrAbove", 2: "AtOrBelow", 3: "EqualTo" } as any)[
              g.targetDirection
            ] ?? "AtOrBelow")
          : (g.targetDirection ?? undefined),
      notes: g.notes || "",
    };
  });
}

function buildPrescriptions(plan: any): TreatmentPlanPrescription[] {
  const enriched: any[] = plan?._enrichedPrescriptions || [];
  if (enriched.length > 0) {
    return enriched.map((p: any) => ({
      medication:
        p?.data?.prescriptionItems?.[0]?.medication ||
        p?.data?.items?.[0]?.medication ||
        p?.data?.items?.[0]?.name ||
        "Unknown",
      dosage:
        p?.data?.prescriptionItems?.[0]?.dosage ||
        p?.data?.items?.[0]?.dosage ||
        "—",
      quantity:
        p?.data?.prescriptionItems?.[0]?.quantity ||
        p?.data?.items?.[0]?.quantity ||
        "—",
    }));
  }
  return (plan?.linkedPrescriptions || []).map((_: any, i: number) => ({
    medication: `Prescription ${i + 1}`,
    dosage: "—",
    quantity: "—",
  }));
}

function buildAppointments(plan: any): TreatmentPlanAppointment[] {
  const appts: any[] = plan?.followUpAppointments || [];
  if (appts.length === 0) return [];

  return appts.map((a: any) => {
    // Support both new full-object shape and old legacy enriched shape
    const data = a?.data ?? a; // legacy _enrichedAppointments had { id, data: {...} }
    
    let timeStr = "—";
    if (data?.time) {
      timeStr = data?.time;
    } else if (data?.startDate) {
      // Extract time directly from ISO string (e.g., "2026-06-30T11:00:00Z" → "11:00")
      try {
        const isoString = typeof data?.startDate === "string" ? data?.startDate : new Date(data?.startDate).toISOString();
        const timePart = isoString.split("T")[1];
        if (timePart) {
          timeStr = timePart.substring(0, 5); // Gets "HH:MM"
        }
      } catch {
        timeStr = "—";
      }
    }
    
    return {
      date: data?.date
        ? new Date(data.date).toLocaleDateString()
        : data?.startDate
          ? new Date(data.startDate).toLocaleDateString()
          : "—",
      time: timeStr,
      type: data?.serviceType || data?.type || "Appointment",
    };
  });
}

function StatusBadge({
  status,
  isDarkMode,
}: {
  status: string;
  isDarkMode: boolean;
}) {
  const map: Record<string, string> = {
    active: "bg-green-100 text-green-700",
    pending: "bg-yellow-100 text-yellow-700",
    achieved: "bg-blue-100 text-blue-700",
    completed: "bg-blue-100 text-blue-700",
    cancelled: "bg-red-100 text-red-700",
  };
  const cls = map[status?.toLowerCase()] ?? "bg-gray-100 text-gray-600";
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${cls}`}>
      {status}
    </span>
  );
}

function SectionTable({
  icon,
  heading,
  isDarkMode,
  children,
}: {
  icon: React.ReactNode;
  heading: string;
  isDarkMode: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-3 overflow-x-auto rounded-lg border border-inherit">
      <table
        className={`w-full text-sm border-collapse ${
          isDarkMode ? "border-gray-600" : "border-gray-200"
        }`}
      >
        <thead>
          <tr className={isDarkMode ? "bg-gray-700/60" : "bg-gray-50"}>
            <th
              colSpan={99}
              className={`px-3 py-2 text-left font-semibold ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              <span className="flex items-center gap-1.5">
                {icon}
                {heading}
              </span>
            </th>
          </tr>
        </thead>
        {children}
      </table>
    </div>
  );
}

function Th({
  children,
  isDarkMode,
}: {
  children: React.ReactNode;
  isDarkMode: boolean;
}) {
  return (
    <th
      className={`border px-2 py-1.5 text-left text-xs font-semibold whitespace-nowrap ${
        isDarkMode
          ? "border-gray-600 bg-gray-700/40 text-gray-300"
          : "border-gray-200 bg-gray-50 text-gray-600"
      }`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  isDarkMode,
}: {
  children: React.ReactNode;
  isDarkMode: boolean;
}) {
  return (
    <td
      className={`border px-2 py-1.5 text-xs ${
        isDarkMode
          ? "border-gray-600 text-gray-400"
          : "border-gray-200 text-gray-600"
      }`}
    >
      {children}
    </td>
  );
}

function TreatmentPlanCard({
  item,
  index,
  items,
  isDarkMode,
  onDeletePlan,
  consultationId,
  consultationData,
}: {
  item: TreatmentPlanItem;
  index: number;
  items: TreatmentPlanItem[];
  isDarkMode: boolean;
  onDeletePlan?: () => void;
  consultationId?: string;
  consultationData?: any;
}) {
  const plan = useMemo(() => parsePlanPayload(item), [item]);
  const overview = buildOverview(plan);
  const goals = buildGoals(plan);
  const prescriptions = buildPrescriptions(plan);
  const appointments = buildAppointments(plan);

  const borderCls = isDarkMode ? "border-gray-700" : "border-gray-200";
  const rowHover = isDarkMode ? "hover:bg-gray-700/30" : "hover:bg-gray-50";

  const existingPlanForModal = useMemo(
    () => ({
      ...plan,
      id: plan?.treatmentPlanId || plan?.id || `tp-${index}`,
    }),
    [plan],
  );

  const existingTreatmentPlanId = plan?.treatmentPlanId || plan?.id || null;
  const resolvedConsultationId = plan?.consultationId || consultationId || "";
  const resolvedPatientId = plan?.patientId || "";

  const matchedActionId = useMemo(() => {
    if (!existingTreatmentPlanId) return null;

    // Look in consultationData.actions — this is where the real DB actionID lives
    const consultationActions = consultationData?.actions || [];
    const matched = consultationActions.find((action: any) => {
      if (
        action.actionName !== "CreateTreatmentPlan" &&
        action.actionName !== "UpdateTreatmentPlan"
      )
        return false;
      try {
        const parsed = JSON.parse(action.payload || "{}");
        return parsed?.treatmentPlanId === existingTreatmentPlanId;
      } catch {
        return false;
      }
    });

    return matched?.actionID ?? matched?.ActionID ?? null;
  }, [consultationData, existingTreatmentPlanId]);

  const cardContent = (
    <div
      className={`group relative rounded-lg border cursor-pointer transition-all ${borderCls} ${
        isDarkMode
          ? "bg-gray-800/50 hover:bg-gray-800"
          : "bg-white hover:bg-gray-50"
      }`}
    >
      {/* Delete — absolute top-right, separate from modal open */}
      {onDeletePlan && (
        <div
          className="absolute top-3 right-3 z-20 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                className={`p-1 rounded transition-colors ${
                  isDarkMode
                    ? "hover:bg-red-900/20 text-red-400 hover:text-red-300"
                    : "hover:bg-red-50 text-red-600 hover:text-red-700"
                }`}
                title="Delete treatment plan"
                onClick={(e) => e.stopPropagation()}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Treatment Plan</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete &quot;{overview.name}&quot;?
                  This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!existingTreatmentPlanId || !resolvedConsultationId) {
                      console.error(
                        "Missing treatmentPlanId/consultationId for treatment plan delete",
                      );
                      return;
                    }

                    if (!resolvedPatientId) {
                      console.error(
                        "Missing patientId for treatment plan delete",
                      );
                      return;
                    }

                    if (!matchedActionId) {
                      console.error(
                        "Could not resolve matched actionId for treatment plan delete",
                      );
                      return;
                    }

                    await deleteTreatmentPlanAction(
                      existingTreatmentPlanId,
                      resolvedConsultationId,
                      resolvedPatientId,
                      matchedActionId,
                    );
                    removePatientAIChatMessageByPlanId(existingTreatmentPlanId);
                    await getPatientConsultationByConsultationId(
                      resolvedConsultationId,
                    );
                    await getPatientActiveTreatmentPlan(resolvedPatientId);
                    onDeletePlan?.();
                  }}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {/* Header */}
      <div
        className={`flex items-center gap-2 px-3 py-2.5 pr-10 border-b ${borderCls}`}
      >
        <ClipboardList
          className={`w-4 h-4 flex-shrink-0 ${
            isDarkMode ? "text-gray-400" : "text-gray-500"
          }`}
        />
        <p
          className={`text-sm font-semibold truncate flex-1 ${
            isDarkMode ? "text-gray-200" : "text-gray-800"
          }`}
        >
          {overview.name}
        </p>
        <StatusBadge
          status={overview.status ?? "active"}
          isDarkMode={isDarkMode}
        />
        {item.isDraft && (
          <Badge className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-700">
            Draft
          </Badge>
        )}
        <div className="flex items-center gap-1 ml-1 flex-shrink-0">
          {overview.goalsCount > 0 && (
            <span
              className={`text-xs px-1.5 py-0.5 rounded-full ${
                isDarkMode
                  ? "bg-gray-700 text-gray-300"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {overview.goalsCount}g
            </span>
          )}
          {overview.appointmentsCount > 0 && (
            <span
              className={`text-xs px-1.5 py-0.5 rounded-full ${
                isDarkMode
                  ? "bg-gray-700 text-gray-300"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {overview.appointmentsCount}a
            </span>
          )}
        </div>
      </div>

      {/* Body — flat tables, always visible */}
      <div className="px-3 pb-3">
        <SectionTable
          icon={
            <FileText
              className={`w-3.5 h-3.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            />
          }
          heading="Plan Overview"
          isDarkMode={isDarkMode}
        >
          <tbody>
            {overview.description && (
              <tr className={rowHover}>
                <Th isDarkMode={isDarkMode}>Description</Th>
                <Td isDarkMode={isDarkMode}>{overview.description}</Td>
              </tr>
            )}
            {overview.startDate && (
              <tr className={rowHover}>
                <Th isDarkMode={isDarkMode}>Start Date</Th>
                <Td isDarkMode={isDarkMode}>
                  {new Date(overview.startDate).toLocaleDateString()}
                </Td>
              </tr>
            )}
            {overview.endDate && (
              <tr className={rowHover}>
                <Th isDarkMode={isDarkMode}>End Date</Th>
                <Td isDarkMode={isDarkMode}>
                  {new Date(overview.endDate).toLocaleDateString()}
                </Td>
              </tr>
            )}
            <tr className={rowHover}>
              <Th isDarkMode={isDarkMode}>Total Actions Planned</Th>
              <Td isDarkMode={isDarkMode}>{overview.totalActions}</Td>
            </tr>
            {overview.goalsCount > 0 && (
              <tr className={rowHover}>
                <Th isDarkMode={isDarkMode}>Goals</Th>
                <Td isDarkMode={isDarkMode}>{overview.goalsCount}</Td>
              </tr>
            )}
            {overview.appointmentsCount > 0 && (
              <tr className={rowHover}>
                <Th isDarkMode={isDarkMode}>Follow-up Appointments</Th>
                <Td isDarkMode={isDarkMode}>{overview.appointmentsCount}</Td>
              </tr>
            )}
            {overview.prescriptionsCount > 0 && (
              <tr className={rowHover}>
                <Th isDarkMode={isDarkMode}>Linked Prescriptions</Th>
                <Td isDarkMode={isDarkMode}>{overview.prescriptionsCount}</Td>
              </tr>
            )}
            {overview.savedAt && (
              <tr className={rowHover}>
                <Th isDarkMode={isDarkMode}>Last Saved</Th>
                <Td isDarkMode={isDarkMode}>
                  {new Date(overview.savedAt).toLocaleString()}
                </Td>
              </tr>
            )}
          </tbody>
        </SectionTable>

        {goals.length > 0 && (
          <SectionTable
            icon={
              <Target
                className={`w-3.5 h-3.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              />
            }
            heading="Clinical Goals"
            isDarkMode={isDarkMode}
          >
            <thead>
              <tr className={isDarkMode ? "bg-gray-700/40" : "bg-gray-50"}>
                <Th isDarkMode={isDarkMode}>Goal</Th>
                <Th isDarkMode={isDarkMode}>Target</Th>
                <Th isDarkMode={isDarkMode}>Date</Th>
                <Th isDarkMode={isDarkMode}>Status</Th>
              </tr>
            </thead>
            <tbody>
              {goals.map((g, i) => (
                <tr key={g.id ?? i} className={rowHover}>
                  <Td isDarkMode={isDarkMode}>{g.description}</Td>
                  <Td isDarkMode={isDarkMode}>
                    {g.targetDirection && (
                      <span className="mr-0.5">
                        {getTargetDirectionSymbol(g.targetDirection as any)}
                      </span>
                    )}
                    {g.targetValue || "—"}
                  </Td>
                  <Td isDarkMode={isDarkMode}>{g.targetDate}</Td>
                  <Td isDarkMode={isDarkMode}>
                    <StatusBadge
                      status={g.status ?? "pending"}
                      isDarkMode={isDarkMode}
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </SectionTable>
        )}

        {prescriptions.length > 0 && (
          <SectionTable
            icon={
              <Pill
                className={`w-3.5 h-3.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              />
            }
            heading="Linked Prescriptions"
            isDarkMode={isDarkMode}
          >
            <thead>
              <tr className={isDarkMode ? "bg-gray-700/40" : "bg-gray-50"}>
                <Th isDarkMode={isDarkMode}>Medication</Th>
                <Th isDarkMode={isDarkMode}>Dosage</Th>
                <Th isDarkMode={isDarkMode}>Quantity</Th>
              </tr>
            </thead>
            <tbody>
              {prescriptions.map((p, i) => (
                <tr key={i} className={rowHover}>
                  <Td isDarkMode={isDarkMode}>
                    {p.medication}
                    {(p.additionalCount ?? 0) > 0 &&
                      ` +${p.additionalCount} more`}
                  </Td>
                  <Td isDarkMode={isDarkMode}>{p.dosage}</Td>
                  <Td isDarkMode={isDarkMode}>{p.quantity}</Td>
                </tr>
              ))}
            </tbody>
          </SectionTable>
        )}

        {appointments.length > 0 && (
          <SectionTable
            icon={
              <Calendar
                className={`w-3.5 h-3.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              />
            }
            heading="Follow-up Appointment Schedule"
            isDarkMode={isDarkMode}
          >
            <thead>
              <tr className={isDarkMode ? "bg-gray-700/40" : "bg-gray-50"}>
                <Th isDarkMode={isDarkMode}>Date</Th>
                <Th isDarkMode={isDarkMode}>Time</Th>
                <Th isDarkMode={isDarkMode}>Type</Th>
              </tr>
            </thead>
            <tbody>
              {appointments.map((a, i) => (
                <tr key={i} className={rowHover}>
                  <Td isDarkMode={isDarkMode}>{a.date}</Td>
                  <Td isDarkMode={isDarkMode}>{a.time}</Td>
                  <Td isDarkMode={isDarkMode}>{a.type}</Td>
                </tr>
              ))}
            </tbody>
          </SectionTable>
        )}
      </div>
    </div>
  );

  return (
    <TreatmentPlanModal
      existingPlan={existingPlanForModal}
      editMode={true}
      isDarkMode={isDarkMode}
      trigger={cardContent}
      onSave={(updatedPlan) => {}}
      fromConsultationActions={true}
      consultationActionId={matchedActionId}
    />
  );
}

export const TreatmentPlanActionItems = ({
  items,
  isDarkMode,
  onPlanClick,
  onDeletePlan,
  consultationId,
  consultationData,
}: TreatmentPlanActionItemsProps) => {
  const validItems = items.filter((item) => item.details !== null);
  const sortedItems = [...validItems].sort((a, b) => {
    const da = new Date(a.displayTimestamp || a.recordedAt || 0).getTime();
    const db = new Date(b.displayTimestamp || b.recordedAt || 0).getTime();
    return da - db;
  });

  if (sortedItems.length === 0) return null;

  return (
    <div className="px-3 pb-3 space-y-3">
      {sortedItems.map((item, index) => (
        <TreatmentPlanCard
          key={index}
          item={item}
          index={index}
          items={sortedItems}
          isDarkMode={isDarkMode}
          onDeletePlan={onDeletePlan}
          consultationId={consultationId}
          consultationData={consultationData}
        />
      ))}
    </div>
  );
};
